import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, IconButton, Tooltip,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { adminClient } from '../data/adminClient';
import { MUSIC_ITEMS, musicLabel } from '../../shared/workshopOptions';
import { spaceHas } from '../../shared/workshopScheduler';
import { useEventReadOnly } from '../adminAccess';

// Admin › Workshops › Spaces. The rooms workshops can use this event: how
// many dancers each holds and what equipment it has (same checklist leaders
// tick for their workshops, so the Schedule can warn about mismatches).
export default function SpacesSection() {
  const readOnly = useEventReadOnly(); // view-only admin: no add/edit/copy
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // space object, or {} for new
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await adminClient.getWorkshopSchedule()); } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const spaces = data?.spaces || [];
  const totalSeats = spaces.reduce((n, s) => n + s.maxDancers, 0);

  const copyPrevious = async () => {
    setBusy(true); setError(null);
    try { await adminClient.copyPreviousWorkshopSpaces(); await load(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
          {data ? `${spaces.length} space${spaces.length === 1 ? '' : 's'} · ${totalSeats} dancers if every space is used · ${data.dancerCount} dancers registered` : ''}
        </Typography>
        <Tooltip title="Refresh"><span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
        {!readOnly && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})} disabled={!data}>Add space</Button>}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : spaces.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>No spaces for this event yet.</Typography>
          {!readOnly && <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            {data?.hasPreviousSpaces && (
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyPrevious} disabled={busy}>Copy spaces from last event</Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})}>Add space</Button>
          </Box>}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Space</TableCell>
                <TableCell align="right">Max dancers</TableCell>
                <TableCell>Equipment</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {spaces.map(s => (
                <TableRow key={s.spaceId} hover sx={readOnly ? undefined : { cursor: 'pointer' }} onClick={readOnly ? undefined : () => setEditing(s)}>
                  <TableCell><Typography variant="body2" fontWeight={500}>{s.name}</Typography></TableCell>
                  <TableCell align="right">{s.maxDancers}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {s.equipment.length ? s.equipment.map(e => <Chip key={e} size="small" label={e} />) : <Typography variant="caption" color="text.disabled">None</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{s.notes}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {data && spaces.length > 0 && <SetupList data={data} onEditSpace={readOnly ? null : setEditing} />}

      {editing && (
        <SpaceDialog space={editing} equipmentOptions={data.equipmentOptions}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </>
  );
}

// What each space needs for the workshops scheduled in it (saved schedule),
// per session: equipment it already has (✓) and what has to be brought in.
function SetupList({ data, onEditSpace }) {
  const wById = new Map(data.workshops.map(w => [w.workshopId, w]));
  const runAt = (spaceId, session) => data.runs.find(r => r.spaceId === spaceId && r.session === session && wById.has(r.workshopId));
  const has = spaceHas;
  const needsOf = (w) => w.setupNeeds || w.equipmentNeeded || [];
  // Music: any one of the leader's ways will do. If none is in the space,
  // suggest bringing the way that's missing the fewest items.
  const musicStatus = (w, sp) => {
    const options = w.musicOptions || [];
    if (!options.length) return null;
    const ok = options.find(o => o.every(item => has(sp, item)));
    if (ok) return { ok: true, label: `✓ Music: ${ok.join(' + ')}` };
    const missingIn = (o) => o.filter(item => !has(sp, item));
    const cheapest = [...options].sort((a, b) => missingIn(a).length - missingIn(b).length)[0];
    return { ok: false, label: `Bring: ${options.map(o => o.join(' + ')).join(' or ')}`, bring: missingIn(cheapest) };
  };

  // Event-wide: how many of each item to bring in, per session (an item
  // missing from two rooms at once needs two).
  const toBring = new Map();
  [1, 2].forEach(session => {
    const counts = new Map();
    data.spaces.forEach(sp => {
      const w = runAt(sp.spaceId, session) && wById.get(runAt(sp.spaceId, session).workshopId);
      if (!w) return;
      [...needsOf(w).filter(item => !has(sp, item)), ...(musicStatus(w, sp)?.bring || [])]
        .forEach(item => counts.set(item, (counts.get(item) || 0) + 1));
    });
    counts.forEach((n, item) => toBring.set(item, Math.max(toBring.get(item) || 0, n)));
  });
  const scheduled = data.runs.some(r => wById.has(r.workshopId));

  return (
    <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
      <Typography fontWeight={600}>Space setup</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        What each space needs for the workshops scheduled in it (from the saved schedule).
        Items with ✓ are already in the space; the rest need to be brought in.{onEditSpace && ' Click a space to update its equipment.'}
      </Typography>

      {!scheduled ? (
        <Typography variant="body2" color="text.secondary">Nothing is on the schedule yet — this fills in once workshops are placed on the Schedule page.</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
            <Typography variant="body2" fontWeight={500}>To bring in:</Typography>
            {toBring.size === 0
              ? <Chip size="small" color="success" label="Nothing — every space has what its workshops need" />
              : [...toBring].sort((a, b) => a[0].localeCompare(b[0])).map(([item, n]) => (
                <Chip key={item} size="small" color="warning" label={n > 1 ? `${item} × ${n}` : item} />
              ))}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 160 }}>Space</TableCell>
                  {[1, 2].map(s => (
                    <TableCell key={s}>{data.sessions[s].name}{data.sessions[s].time ? ` · ${data.sessions[s].time}` : ''}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.spaces.map(sp => (
                  <TableRow key={sp.spaceId} hover sx={{ cursor: onEditSpace ? 'pointer' : undefined, verticalAlign: 'top' }} onClick={onEditSpace ? () => onEditSpace(sp) : undefined}>
                    <TableCell><Typography variant="body2" fontWeight={500}>{sp.name}</Typography></TableCell>
                    {[1, 2].map(session => {
                      const run = runAt(sp.spaceId, session);
                      const w = run && wById.get(run.workshopId);
                      if (!w) return <TableCell key={session}><Typography variant="caption" color="text.disabled">No workshop</Typography></TableCell>;
                      const needs = needsOf(w);
                      const music = musicStatus(w, sp);
                      return (
                        <TableCell key={session}>
                          <Typography variant="body2">{w.title}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {[w.leaderName, musicLabel(w.musicSetup)].filter(Boolean).join(' · ')}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {needs.length === 0 && !music
                              ? <Typography variant="caption" color="text.disabled">No equipment listed</Typography>
                              : needs.map(item => (has(sp, item)
                                ? <Chip key={item} size="small" variant="outlined" color="success" label={`✓ ${item}`} />
                                : <Chip key={item} size="small" color="warning" label={`Bring: ${item}`} />))}
                            {music && (music.ok
                              ? <Chip size="small" variant="outlined" color="success" label={music.label} />
                              : <Chip size="small" color="warning" label={music.label} />)}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
}

function SpaceDialog({ space, equipmentOptions, onClose, onSaved }) {
  const isNew = !space.spaceId;
  const [form, setForm] = useState({ name: space.name || '', maxDancers: space.maxDancers || '', equipment: space.equipment || [], notes: space.notes || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // The checklist plus the music items workshops can need (speaker, adapters…).
  const options = [...new Set([...equipmentOptions, 'Speaker', ...MUSIC_ITEMS, ...form.equipment])];
  const toggle = (item) => setForm(f => ({ ...f, equipment: f.equipment.includes(item) ? f.equipment.filter(x => x !== item) : [...f.equipment, item] }));

  const run = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); onSaved(); } catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Add space' : `Edit ${space.name}`}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2, pt: 1 }}>
          <TextField size="small" label="Name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} inputProps={{ maxLength: 80 }} autoFocus />
          <TextField size="small" label="Max dancers" required type="number" value={form.maxDancers}
            onChange={e => setForm({ ...form, maxDancers: e.target.value })} inputProps={{ min: 1, max: 1000 }} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Equipment in this space</Typography>
        <FormGroup row>
          {options.map(item => (
            <FormControlLabel key={item} control={<Checkbox size="small" checked={form.equipment.includes(item)} onChange={() => toggle(item)} />}
              label={<Typography variant="body2">{item}</Typography>} />
          ))}
        </FormGroup>
        <Typography variant="caption" color="text.secondary">Edit this checklist with “Equipment list” on the Workshop Leaders page.</Typography>
        <TextField size="small" label="Notes" fullWidth multiline minRows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
          inputProps={{ maxLength: 500 }} sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!isNew ? (confirmDelete ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Delete? Any workshops scheduled here go back to unplaced.</Typography>
            <Button size="small" color="error" variant="contained" disabled={busy} onClick={() => run(() => adminClient.deleteWorkshopSpace(space.spaceId))}>Delete</Button>
            <Button size="small" onClick={() => setConfirmDelete(false)}>Keep</Button>
          </Box>
        ) : <Button color="error" onClick={() => setConfirmDelete(true)}>Delete space</Button>) : <span />}
        <Box>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={busy || !form.name.trim() || !(Number(form.maxDancers) > 0)}
            onClick={() => run(() => (isNew ? adminClient.addWorkshopSpace(form) : adminClient.saveWorkshopSpace(space.spaceId, form)))}>
            {busy ? <CircularProgress size={18} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
